/**
 * 연락처 추가 · 수정 (2026-09-08 · 주소록 판).
 *
 * ★ 이름은 **비워도 된다** — 「(이름없음)」으로 담긴다. 주소만 아는 사람이 가장 많아서,
 *   이름을 필수로 두면 사람들이 주소를 이름 칸에 적어 넣는다.
 * ★ 필수는 메일주소 하나다.
 * ★ 공유 주소록에 **담는** 곳은 모두 고를 수 있다 (2026-09-15 · 서버 #1374 — 종전: 관리자만).
 *   **고치기 · 지우기**는 여전히 관리자라, 공유에 이미 있는 주소를 일반 사용자가 담으면 서버가
 *   `409 ALREADY_EXISTS · editable:false` 를 주고 이 창은 「관리자에게 요청하세요」라고 말한다.
 */

import { useCallback, useEffect, useRef, useState, type ChangeEvent } from 'react';
import { Modal, ModalActions } from '../../components/shared/Modal';
import { Button } from '../../components/shared/Button';
import { Input } from '../../components/shared/Input';
import { FormField } from '../../components/shared/FormField';
import { Select } from '../../components/shared/Select';
import { MultiValueInput } from '../../components/shared/MultiValueInput';
import { Star } from '../../icons';
import { contactsApi, type Contact, type ContactScope, type ContactTag } from '../../api/contacts';
import { tContacts } from '../../lib/contacts-i18n';
import { ContactPhoto } from './ContactPhoto';
import { CONTACT_PHOTO_ACCEPT, isContactPhotoMime, uploadContactPhoto } from './contact-photo-upload';

export interface ContactEditModalProps {
  /** 고칠 연락처. 없으면 새로 담는 판이다. */
  contact?: Contact | null;
  /** 새로 담을 때 미리 채우는 값 — 메일에서 「담기」를 눌러 들어온 자리. */
  prefill?: { email?: string; name?: string | null };
  /** 담을 곳의 기본값. */
  defaultScope?: ContactScope;
  /** 담을 곳에서 공유 주소록을 고를 수 있나 — 2026-09-15 부터 모두(담기만 · 고치기는 관리자). */
  canShare: boolean;
  tags: ContactTag[];
  onClose: () => void;
  onSaved: (contact: Contact) => void;
}

const EMAIL_SHAPE = /^[^\s@,;<>"]+@[^\s@,;<>"]+\.[^\s@,;<>"]{2,}$/;

export function ContactEditModal(props: ContactEditModalProps) {
  const { contact, prefill, canShare, tags, onClose, onSaved } = props;
  const editing = contact != null;

  const [email, setEmail] = useState(contact?.email ?? prefill?.email ?? '');
  const [name, setName] = useState(contact?.name ?? prefill?.name ?? '');
  const [phone, setPhone] = useState(contact?.phone ?? '');
  const [mobile, setMobile] = useState(contact?.mobile ?? '');
  const [company, setCompany] = useState(contact?.company ?? '');
  const [department, setDepartment] = useState(contact?.department ?? '');
  const [position, setPosition] = useState(contact?.position ?? '');
  const [note, setNote] = useState(contact?.note ?? '');
  const [starred, setStarred] = useState(contact?.isStarred ?? false);
  const [scope, setScope] = useState<ContactScope>(contact?.scope ?? props.defaultScope ?? 'PERSONAL');
  const [tagNames, setTagNames] = useState<string[]>(contact?.tags.map((t) => t.name) ?? []);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  /**
   * 사진을 어떻게 할 것인가 — 서버 PATCH 의 **세 갈래와 1:1** 이다.
   *   `keep` = 칸 자체를 안 싣는다(안 건드림) · `remove` = `null` 을 보낸다(뗀다) ·
   *   `file` = 「담기」 때 **그때 올려서** 받은 번호를 보낸다(붙인다)
   *
   * ⭐⭐ 2026-10-06 사용자 결정 — 「파일을 선택해도 «저장하지 않으면» 올라가면 안된다」.
   *    그래서 `file` 은 **번호가 아니라 `File` 그 자체**를 들고 있다. 고른 순간 서버에는
   *    아무것도 없고, `url`(로컬 objectURL)이 그 동안의 그림을 맡는다.
   * ⚠ 내가 #2212 에서 고를 때 올리도록 지은 것을 이 판이 되돌린다 — 그때는 「미리보기를
   *   잃는다」고 적었는데 **틀렸다**(`URL.createObjectURL` 로 올리지 않고도 그려진다).
   */
  type PhotoDraft =
    | { kind: 'keep' }
    | { kind: 'remove' }
    | { kind: 'file'; file: File; url: string };
  const [photoDraft, setPhotoDraft] = useState<PhotoDraft>({ kind: 'keep' });
  const photoInputRef = useRef<HTMLInputElement>(null);
  /** 담기는 됐고 사진만 실패했을 때의 id — 다시 누를 때 POST 를 되풀이하지 않게 한다. */
  const createdIdRef = useRef<string | null>(null);

  /**
   * 로컬 미리보기 주소는 **반드시 치운다** — 안 치우면 그 탭이 파일을 메모리에 붙들고 있다.
   * 꼴은 `AvatarUploader.tsx:44` 그대로다: 값이 바뀔 때 «옛» 주소를, 창이 닫힐 때 지금 주소를.
   */
  const draftUrl = photoDraft.kind === 'file' ? photoDraft.url : null;
  useEffect(() => () => { if (draftUrl) URL.revokeObjectURL(draftUrl); }, [draftUrl]);

  /**
   * 동그라미에 넘길 번호. **뗄 때만** null 이다.
   *
   * ⚠ 고른 파일이 있을 때도 «번호를 그대로 넘긴다» — 그려지는 것은 `previewUrl` 이 이기므로
   *   화면은 같다. 그런데 번호를 여기서 null 로 바꿔 버리면 `ContactPhoto` 의
   *   「미리보기가 있으면 presigned 를 안 부른다」가 **도달 불가**가 되어, 그 조건이 죽은 줄이
   *   된다(2026-10-06 사보타주 N 이 안 갈려서 찾았다). 번호를 넘겨야 그 조건이 일을 한다.
   */
  const shownPhotoFileId = photoDraft.kind === 'remove' ? null : contact?.photoFileId ?? null;
  const hasPhotoToShow = photoDraft.kind === 'file' || shownPhotoFileId != null;

  /**
   * 파일을 고르면 **올리지 않고** 미리보기만 세운다 (위 사용자 결정).
   * ⇒ 그래서 여기서 할 수 있는 판정은 mime 하나다. 용량·한도는 올릴 때 서버가 말해 준다.
   */
  const onPickPhoto = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // 같은 파일을 다시 골라도 onChange 가 오게 비운다
    if (!file) return;
    setError(null);
    if (!isContactPhotoMime(file.type)) {
      setError(tContacts('contacts.edit.photo_bad_type'));
      return;
    }
    const url = URL.createObjectURL(file);
    setPhotoDraft((prev) => {
      if (prev.kind === 'file') URL.revokeObjectURL(prev.url);
      return { kind: 'file', file, url };
    });
  }, []);

  /* 열릴 때마다 새 값으로 — 같은 모달을 다른 사람에게 다시 쓰는 자리가 있다. */
  useEffect(() => {
    setError(null);
  }, [contact, prefill]);

  /** 태그는 이름으로 다룬다 — 없으면 만들고, 있으면 그 id 를 쓴다. */
  const resolveTagIds = useCallback(async (names: string[]): Promise<string[]> => {
    const out: string[] = [];
    for (const raw of names) {
      const n = raw.trim();
      if (n === '') continue;
      const found = tags.find((t) => t.name === n);
      if (found) { out.push(found.id); continue; }
      try {
        const created = await contactsApi.createTag(n);
        out.push(created.id);
      } catch {
        /* 태그 하나를 못 만들었다고 연락처를 못 담게 하지 않는다 — 그 태그만 빠진다. */
      }
    }
    return out;
  }, [tags]);

  const submit = useCallback(async () => {
    const addr = email.trim().toLowerCase();
    if (addr === '') { setError(tContacts('contacts.edit.email_required')); return; }
    if (!EMAIL_SHAPE.test(addr)) { setError(tContacts('contacts.edit.email_invalid')); return; }

    setSaving(true);
    setError(null);
    /** 어느 걸음에서 깨졌나 — 세 실패가 사용자에게 «다른 말»이어야 한다. */
    let phase: 'fields' | 'upload' = 'fields';
    try {
      const tagIds = await resolveTagIds(tagNames);
      const payload = {
        name: name.trim() || null,
        phone: phone.trim() || null,
        mobile: mobile.trim() || null,
        company: company.trim() || null,
        department: department.trim() || null,
        position: position.trim() || null,
        note: note.trim() || null,
        isStarred: starred,
        tagIds,
      };

      /*
       * ⭐ 올리기는 **여기서** 한다 — 「저장하지 않으면 올라가면 안된다」(사용자 결정).
       *   ⇒ 고르기만 하고 창을 닫으면 **아무것도 올라가지 않는다** ⇒ 고아 파일 0.
       */
      const existingId = contact?.id ?? createdIdRef.current;

      if (existingId) {
        /* 고치기 — 올린 뒤 **한 번의** PATCH 에 칸까지 함께 싣는다. */
        const photo = photoDraft.kind === 'file'
          ? { photoFileId: await (async () => { phase = 'upload'; const id = await uploadContactPhoto(photoDraft.file); phase = 'fields'; return id; })() }
          : photoDraft.kind === 'remove' ? { photoFileId: null } : {};
        onSaved(await contactsApi.update(existingId, { ...payload, ...photo }));
        return;
      }

      /*
       * 새로 담기 (2026-10-06 사용자 결정 ② 「네」) — ⛔ **두 걸음이어야 한다.**
       *   서버 `POST /contacts` 본문 스키마에 `photoFileId` 가 0건이고
       *   `additionalProperties:false` 라, 함께 보내면 400 이 아니라 **조용히 지워진다.**
       *   ⇒ 그래서 `POST` 로 id 를 받고 `PATCH` 로 사진을 붙인다(관제 권고 ⓘ · 서버 무변경).
       * ★ 순서가 «POST 먼저»인 까닭: 담기의 흔한 실패는 **주소 중복(409)** 이다.
       *   먼저 올렸다가 중복으로 막히면 그때마다 고아가 생긴다.
       */
      const created = await contactsApi.create({ ...payload, email: addr, scope });
      if (photoDraft.kind !== 'file') { onSaved(created); return; }

      /*
       * ⛔ 여기서부터는 **명함이 이미 담겼다.** 사진만 실패하면 창을 닫지 않고 그 말을 하고,
       *   `createdIdRef` 에 id 를 남겨 **다시 누를 때 POST 를 되풀이하지 않게** 한다
       *   (되풀이하면 그 다음엔 `ALREADY_EXISTS` 가 뜬다 — 사용자는 까닭을 알 수 없다).
       */
      createdIdRef.current = created.id;
      phase = 'upload';
      const fileId = await uploadContactPhoto(photoDraft.file);
      phase = 'fields';
      onSaved(await contactsApi.update(created.id, { photoFileId: fileId }));
    } catch (err) {
      const e = err as { response?: { data?: { code?: string; editable?: boolean; message?: string } } };
      const data = e.response?.data;
      /*
       * ⭐⭐ 실패를 **셋으로** 가른다. 뭉개면 사용자가 다음에 무엇을 할지 모른다:
       * ```
       * ⓐ 명함은 담겼고 사진만 못 붙었다  ⇒ 창을 닫지 않는다. 다시 누르면 PATCH 만 간다
       * ⓑ 올리기가 깨졌다                 ⇒ 서버에 아무것도 안 남았다. 다른 파일을 골라라
       * ⓒ 붙이기가 400 이다               ⇒ 파일은 올라갔고 명함에만 안 붙었다(고아 하나)
       * ```
       * ★ 까닭 문구는 **서버 것을 그대로** 쓴다 — `isUsablePhoto` 가 거절하는 까닭이 여섯인데
       *   서버만 어느 것인지 알고, 여기서 지어내면 같은 뜻이 두 글자로 갈린다.
       */
      const reason = data?.message ?? ((err instanceof Error && err.message) || '');
      const say = (head: string) => setError(reason ? `${head} · ${reason}` : head);

      if (!editing && createdIdRef.current != null) {
        say(tContacts('contacts.edit.photo_saved_without'));      // ⓐ
      } else if (phase === 'upload') {
        // ⓑ `uploadChatAttachment` 가 용량·형식·한도를 이미 한국어로 바꿔 준다.
        setError(reason || tContacts('contacts.edit.failed'));
      } else if (data?.code === 'CONTACT_PHOTO_INVALID') {
        say(tContacts('contacts.edit.photo_attach_failed'));       // ⓒ
      } else {
        setError(data?.code === 'ALREADY_EXISTS'
          // ★ 공유에 이미 있고 내가 고칠 수 없는 줄(#1374 `editable:false`) — 「고치러 가기」가 아니라 관리자에게 보낸다.
          ? tContacts(scope === 'SHARED' && data.editable === false ? 'contacts.edit.duplicate_shared' : 'contacts.edit.duplicate')
          : tContacts('contacts.edit.failed'));
      }
    } finally {
      setSaving(false);
    }
  }, [email, name, phone, mobile, company, department, position, note, starred, scope,
    tagNames, editing, contact, onSaved, resolveTagIds, photoDraft]);

  return (
    <Modal
      title={tContacts(editing ? 'contacts.edit.edit' : 'contacts.edit.new')}
      size="sm"
      onClose={onClose}
      footer={(
        <ModalActions>
          <Button
            variant="secondary"
            iconLeft={<Star size={15} aria-hidden />}
            aria-pressed={starred}
            onClick={() => setStarred((v) => !v)}
          >
            {tContacts(starred ? 'contacts.row.star_on' : 'contacts.edit.star')}
          </Button>
          <span style={{ flex: 1 }} />
          <Button variant="secondary" onClick={onClose}>{tContacts('contacts.edit.cancel')}</Button>
          <Button variant="primary" loading={saving} onClick={() => { void submit(); }}>
            {tContacts(editing ? 'contacts.edit.update' : 'contacts.edit.save')}
          </Button>
        </ModalActions>
      )}
    >
      <div className="ctp-form">
        {/*
          2026-10-06 지시 5·6 — 「연락처 수정에도 명함 이미지를 보이도록」 ＋ 「이미지를 수정할 수 있도록」.

          ⭐ **새로 담는 판에도 그린다** (2026-10-06 사용자 결정 ② 「네」).
             #2212 에서는 안 그렸다 — 그때는 원문이 둘 다 「수정」이어서 여쭌 상태였다.
             ⇒ 담기는 id 가 없어 `PATCH` 를 못 부르므로 `submit` 이 **POST → 올리기 → PATCH**
               두 걸음으로 잇는다(그 까닭은 `submit` 의 주석).
        */}
        <FormField label={tContacts('contacts.edit.photo')} className="ctp-form__full">
          <div className="ctp-photo">
            <ContactPhoto
              photoFileId={shownPhotoFileId}
              /* 아직 «안 올린» 파일의 로컬 그림 — 서버 번호를 이긴다. */
              previewUrl={photoDraft.kind === 'file' ? photoDraft.url : null}
              name={contact?.name ?? name}
              variant="lg"
            />
            <div className="ctp-photo__acts">
              <Button variant="secondary" size="sm" onClick={() => photoInputRef.current?.click()}>
                {tContacts(hasPhotoToShow ? 'contacts.edit.photo_change' : 'contacts.edit.photo_pick')}
              </Button>
              {/* 뗄 것이 없으면 단추를 두지 않는다 — 눌러도 아무 일이 없는 단추가 생긴다(정본 F3-2 의 규율). */}
              {hasPhotoToShow && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setPhotoDraft((prev) => {
                    if (prev.kind === 'file') URL.revokeObjectURL(prev.url);
                    // 고른 파일을 물리는 것과 «원래 사진을 떼는» 것은 다르다.
                    // 원래 사진이 없었으면 보낼 것도 없으니 「안 건드림」으로 돌아간다.
                    return contact?.photoFileId ? { kind: 'remove' } : { kind: 'keep' };
                  })}
                >
                  {tContacts('contacts.edit.photo_remove')}
                </Button>
              )}
            </div>
            <input
              ref={photoInputRef}
              type="file"
              accept={CONTACT_PHOTO_ACCEPT}
              onChange={onPickPhoto}
              hidden
              aria-hidden="true"
              tabIndex={-1}
            />
          </div>
        </FormField>
        <FormField label={tContacts('contacts.col.name')}>
          <Input
            value={name}
            placeholder={tContacts('contacts.noname')}
            onChange={(e) => setName(e.target.value)}
            fullWidth
          />
        </FormField>
        <FormField label={tContacts('contacts.col.email')} required error={error ?? undefined}>
          <Input
            value={email}
            type="email"
            /* 담은 뒤에는 주소를 못 바꾼다 — 바꾸면 그건 담기가 아니라 «다른 사람»이다. */
            disabled={editing}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
          />
        </FormField>
        <FormField label={tContacts('contacts.col.phone')}>
          <Input value={phone} onChange={(e) => setPhone(e.target.value)} fullWidth />
        </FormField>
        <FormField label={tContacts('contacts.col.mobile')}>
          <Input value={mobile} onChange={(e) => setMobile(e.target.value)} fullWidth />
        </FormField>
        <FormField label={tContacts('contacts.col.company')}>
          <Input value={company} onChange={(e) => setCompany(e.target.value)} fullWidth />
        </FormField>
        <FormField label={tContacts('contacts.edit.department')}>
          <div className="ctp-form__pair">
            <Input value={department} onChange={(e) => setDepartment(e.target.value)} fullWidth />
            <Input value={position} onChange={(e) => setPosition(e.target.value)} fullWidth />
          </div>
        </FormField>
        <FormField label={tContacts('contacts.col.tags')} className="ctp-form__full">
          <MultiValueInput
            values={tagNames}
            onChange={setTagNames}
            placeholder={tContacts('contacts.edit.tags_ph')}
          />
        </FormField>
        {!editing && canShare && (
          <FormField label={tContacts('contacts.edit.where')} className="ctp-form__full">
            {/* ⚠ 모달 본문은 overflow 상자다 — `portal` 을 안 켜면 목록이 **잘린다**. */}
            <Select
              value={scope}
              portal
              onChange={(v) => setScope(v as ContactScope)}
              options={[
                { value: 'PERSONAL', label: tContacts('contacts.list.personal') },
                { value: 'SHARED', label: tContacts('contacts.list.shared') },
              ]}
            />
          </FormField>
        )}
        <FormField label={tContacts('contacts.edit.note')} className="ctp-form__full">
          <Input
            value={note}
            placeholder={tContacts('contacts.edit.note_ph')}
            onChange={(e) => setNote(e.target.value)}
            fullWidth
          />
        </FormField>
      </div>
    </Modal>
  );
}
